/**
 * Link-to-Xero button + modal for a single stock product.
 *
 * Renders as a compact icon button in the Stock product row's action
 * bar. Colour signals link state at-a-glance:
 *   – emerald tick    = linked (title tooltip shows Xero code + price)
 *   – slate outline   = unlinked
 *
 * Click opens a modal that lets staff:
 *   1. Auto-fill search with the product's SKU
 *   2. Pick a Xero item to link (or unlink an existing link)
 *   3. Push the product to Xero as a new item when nothing matches
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link2, LinkIcon, CheckCircle2, Loader2, Search, Unlink, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export default function LinkToXeroButton({ product, onChanged }) {
  const [open, setOpen] = useState(false);
  const linked = Boolean(product?.xero_item_id);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`xero-link-btn-${product.id}`}
        aria-label={linked ? `Xero-linked to ${product.xero_code}` : 'Link to Xero'}
        title={
          linked
            ? `Linked to Xero: ${product.xero_code}${product.xero_price != null ? ` · £${product.xero_price}` : ''}`
            : 'Link to Xero'
        }
        className={`shrink-0 h-9 w-9 p-0 ${
          linked
            ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
      >
        {linked ? <CheckCircle2 className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </Button>

      {open && (
        <LinkModal
          product={product}
          onClose={() => setOpen(false)}
          onChanged={() => {
            setOpen(false);
            onChanged?.();
          }}
        />
      )}
    </>
  );
}

function LinkModal({ product, onClose, onChanged }) {
  // Search seed = the product's SKU if it has one; otherwise the name.
  const seed = (product.sku || product.name || '').trim();
  const [query, setQuery] = useState(seed);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  const [busyPush, setBusyPush] = useState(false);
  const [busyUnlink, setBusyUnlink] = useState(false);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/xero/items`);
      if (q) url.searchParams.set('q', q);
      url.searchParams.set('limit', '50');
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Search failed');
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { search(seed); }, [seed, search]);

  // Debounce free-typing search so we don't hammer the endpoint.
  useEffect(() => {
    if (query === seed) return;
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, seed, search]);

  // Prefer to surface exact-SKU matches at the top even when the user
  // starts typing something else — makes the "obvious" match one click.
  const orderedItems = useMemo(() => {
    if (!seed) return items;
    const target = seed.trim().toUpperCase();
    const exact = items.filter(i => (i.code || '').trim().toUpperCase() === target);
    const rest = items.filter(i => (i.code || '').trim().toUpperCase() !== target);
    return [...exact, ...rest];
  }, [items, seed]);

  const link = async (xeroItemId) => {
    setBusyItemId(xeroItemId);
    try {
      const res = await fetch(`${API_URL}/api/xero/link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id, xero_item_id: xeroItemId }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Link failed');
      toast.success('Linked to Xero.');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyItemId(null);
    }
  };

  const unlink = async () => {
    setBusyUnlink(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/unlink`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Unlink failed');
      toast.success('Unlinked.');
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyUnlink(false);
    }
  };

  const pushToXero = async () => {
    setBusyPush(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/push`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Push failed');
      toast.success(`Pushed to ${body.tenant_name || 'Xero'} as ${body.item?.code || 'new item'}.`);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyPush(false);
    }
  };

  const currentlyLinkedId = product.xero_item_id;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="xero-link-modal">
        <DialogHeader>
          <DialogTitle>Match to Xero</DialogTitle>
          <div className="text-xs text-gray-500 mt-1">
            Portal product: <strong>{product.name}</strong>{product.sku ? ` (SKU ${product.sku})` : ''}
          </div>
        </DialogHeader>

        {currentlyLinkedId && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-3 mb-2" data-testid="xero-current-link">
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Currently linked</div>
              <div className="text-emerald-800 text-xs">
                Xero code <strong>{product.xero_code || '—'}</strong>
                {product.xero_price != null && <> · £{product.xero_price}</>}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={unlink}
              disabled={busyUnlink}
              data-testid="xero-unlink-btn"
            >
              {busyUnlink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5 mr-1" />}
              Unlink
            </Button>
          </div>
        )}

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Xero items by SKU, name or description"
            className="pl-9"
            data-testid="xero-link-search"
          />
        </div>

        <div className="max-h-[45vh] overflow-y-auto -mx-2 mt-2" data-testid="xero-link-results">
          {loading ? (
            <div className="p-4 text-center text-sm text-slate-500 italic">Searching Xero…</div>
          ) : orderedItems.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500 italic">
              No Xero items match. If this product isn&apos;t in Xero yet, push it as a new item below.
            </div>
          ) : (
            <ul className="space-y-1 px-2">
              {orderedItems.map(it => {
                const exact = seed && (it.code || '').trim().toUpperCase() === seed.trim().toUpperCase();
                const isCurrent = it.xero_item_id === currentlyLinkedId;
                return (
                  <li key={it.xero_item_id} data-testid={`xero-link-row-${it.xero_item_id}`}>
                    <div className={`flex items-center gap-3 p-2 rounded-lg border ${
                      isCurrent ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                          <span className="font-mono">{it.code || '—'}</span>
                          {exact && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">
                              Exact SKU match
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 truncate">{it.name || 'Unnamed item'}</div>
                        {it.sales_price != null && (
                          <div className="text-[11px] text-slate-500 mt-0.5">£{it.sales_price} · {it.sales_tax_type || '—'}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => link(it.xero_item_id)}
                        disabled={busyItemId === it.xero_item_id || isCurrent}
                        data-testid={`xero-link-choose-${it.xero_item_id}`}
                        className={isCurrent ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        {busyItemId === it.xero_item_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                          <><LinkIcon className="w-3.5 h-3.5 mr-1" /> {isCurrent ? 'Linked' : 'Link'}</>
                        )}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="xero-link-cancel">
            Close
          </Button>
          <Button
            onClick={pushToXero}
            disabled={busyPush}
            data-testid="xero-push-btn"
            className="bg-slate-800 hover:bg-slate-900 text-white"
            title="Create this as a new Item in Xero"
          >
            {busyPush ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Push as new Xero item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
